import React, { useState } from 'react';
import { X } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { format } from 'date-fns';
import { nl } from 'date-fns/locale';
import { useAccentColor } from '../lib/useAccentColor';
import { logActivity } from '../lib/activity';

interface RevenueFormProps {
  date: string;
  entry?: {
    id: string;
    cash_revenue: number;
    card_revenue: number;
    invoice_revenue: number;
    notes?: string;
  };
  onClose: () => void;
  onSave: (entry: any) => void;
}

export default function RevenueForm({ date, entry, onClose, onSave }: RevenueFormProps) {
  const { accentColor } = useAccentColor();
  const [formData, setFormData] = useState({
    cash_revenue: entry?.cash_revenue || 0,
    card_revenue: entry?.card_revenue || 0,
    invoice_revenue: entry?.invoice_revenue || 0,
  });

  const [inputValues, setInputValues] = useState({
    cash_revenue: entry?.cash_revenue ? entry.cash_revenue.toFixed(2).replace('.', ',') : '',
    card_revenue: entry?.card_revenue ? entry.card_revenue.toFixed(2).replace('.', ',') : '',
    invoice_revenue: entry?.invoice_revenue ? entry.invoice_revenue.toFixed(2).replace('.', ',') : '',
  });

  const [notes, setNotes] = useState(entry?.notes || '');
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);

    const { data: { user } } = await supabase.auth.getUser();

    if (!user) {
      console.error('No user found');
      setSubmitting(false);
      return;
    }

    try {
      let revenueEntry;
      if (entry?.id) {
        const { data, error: revenueError } = await supabase
          .from('revenue_entries')
          .update({
            cash_revenue: formData.cash_revenue,
            card_revenue: formData.card_revenue,
            invoice_revenue: formData.invoice_revenue,
            notes: notes || null,
            updated_at: new Date().toISOString(),
          })
          .eq('id', entry.id)
          .select()
          .maybeSingle();

        if (revenueError) throw revenueError;
        revenueEntry = data;

        await logActivity(
          'Omzet bijgewerkt',
          `Omzet voor ${format(new Date(date), 'dd MMMM yyyy', { locale: nl })} bijgewerkt`
        );
      } else {
        // Check if entry already exists for this date
        const { data: existingEntry } = await supabase
          .from('revenue_entries')
          .select('id')
          .eq('user_id', user.id)
          .eq('date', date)
          .maybeSingle();

        if (existingEntry) {
          // Update existing entry instead of creating duplicate
          const { data, error: revenueError } = await supabase
            .from('revenue_entries')
            .update({
              cash_revenue: formData.cash_revenue,
              card_revenue: formData.card_revenue,
              invoice_revenue: formData.invoice_revenue,
              notes: notes || null,
              updated_at: new Date().toISOString(),
            })
            .eq('id', existingEntry.id)
            .select()
            .maybeSingle();

          if (revenueError) throw revenueError;
          revenueEntry = data;

          await logActivity(
            'Omzet bijgewerkt',
            `Omzet voor ${format(new Date(date), 'dd MMMM yyyy', { locale: nl })} bijgewerkt`
          );
        } else {
          // Create new entry
          const { data, error: revenueError } = await supabase
            .from('revenue_entries')
            .insert({
              date,
              user_id: user.id,
              cash_revenue: formData.cash_revenue,
              card_revenue: formData.card_revenue,
              invoice_revenue: formData.invoice_revenue,
              notes: notes || null,
            })
            .select()
            .maybeSingle();

          if (revenueError) throw revenueError;
          revenueEntry = data;

          await logActivity(
            'Omzet toegevoegd',
            `Nieuwe omzet toegevoegd voor ${format(new Date(date), 'dd MMMM yyyy', { locale: nl })}`
          );
        }
      }

      onSave(revenueEntry);
    } catch (error) {
      console.error('Error saving data:', error);
    } finally {
      setSubmitting(false);
    }
  };

  const handleInputChange = (field: keyof typeof formData, value: string) => {
    // Verwijder eerst alle niet-numerieke tekens behalve komma
    let cleanValue = value.replace(/[^\d,]/g, '');
    
    // Als er een komma in zit, zorg dat er maximaal 2 decimalen zijn
    if (cleanValue.includes(',')) {
      const [whole, decimal] = cleanValue.split(',');
      
      // Als er decimalen zijn, limiteer tot 2
      if (decimal) {
        // Neem alleen de eerste 2 decimalen
        cleanValue = `${whole},${decimal.slice(0, 2)}`;
      } else {
        cleanValue = `${whole},`;
      }
    }

    // Update de ruwe input waarde
    setInputValues(prev => ({
      ...prev,
      [field]: cleanValue
    }));

    // Voor de opslag in de database, vervang komma door punt en converteer naar nummer
    const numberValue = cleanValue.replace(',', '.');
    setFormData(prev => ({
      ...prev,
      [field]: numberValue === '' || numberValue === '.' ? 0 : parseFloat(numberValue) || 0,
    }));
  };

  // Format the date for display
  const formattedDate = format(new Date(date), 'EEEE d MMMM yyyy', { locale: nl });

  return (
    <div className="fixed inset-0 z-[99999]">
      <div 
        className="fixed inset-0 bg-black/70 backdrop-blur-sm"
        onClick={onClose}
        aria-hidden="true"
      />
      
      <div className="fixed inset-0 overflow-y-auto">
        <div className="flex min-h-full items-center justify-center p-4">
          <div className="relative bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full">
            <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
              <div>
                <h3 className="text-lg font-medium text-gray-900 dark:text-white">
                  {entry ? 'Dienst bewerken' : 'Dienst afronden'}
                </h3>
                <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                  {formattedDate}
                </p>
              </div>
              <button
                onClick={onClose}
                className="text-gray-400 hover:text-gray-500 dark:hover:text-gray-300"
              >
                <X className="h-6 w-6" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              <div>
                <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-4">Omzet</h4>
                <div className="space-y-4">
                  <div>
                    <label
                      htmlFor="cash_revenue"
                      className="block text-sm font-medium text-gray-700 dark:text-gray-300"
                    >
                      Contant (€)
                    </label>
                    <div className="mt-1 relative">
                      <input
                        type="text"
                        inputMode="decimal"
                        id="cash_revenue"
                        value={inputValues.cash_revenue}
                        onChange={(e) => handleInputChange('cash_revenue', e.target.value)}
                        onFocus={(e) => e.target.select()}
                        className="block w-full rounded-lg border border-gray-300 dark:border-gray-600 px-4 py-3 text-gray-900 dark:text-white bg-white dark:bg-gray-700 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20 transition-all duration-200 shadow-sm"
                      />
                    </div>
                  </div>

                  <div>
                    <label
                      htmlFor="card_revenue"
                      className="block text-sm font-medium text-gray-700 dark:text-gray-300"
                    >
                      Pin (€)
                    </label>
                    <div className="mt-1 relative">
                      <input
                        type="text"
                        inputMode="decimal"
                        id="card_revenue"
                        value={inputValues.card_revenue}
                        onChange={(e) => handleInputChange('card_revenue', e.target.value)}
                        onFocus={(e) => e.target.select()}
                        className="block w-full rounded-lg border border-gray-300 dark:border-gray-600 px-4 py-3 text-gray-900 dark:text-white bg-white dark:bg-gray-700 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20 transition-all duration-200 shadow-sm"
                      />
                    </div>
                  </div>

                  <div>
                    <label
                      htmlFor="invoice_revenue"
                      className="block text-sm font-medium text-gray-700 dark:text-gray-300"
                    >
                      Rekening (€)
                    </label>
                    <div className="mt-1 relative">
                      <input
                        type="text"
                        inputMode="decimal"
                        id="invoice_revenue"
                        value={inputValues.invoice_revenue}
                        onChange={(e) => handleInputChange('invoice_revenue', e.target.value)}
                        onFocus={(e) => e.target.select()}
                        className="block w-full rounded-lg border border-gray-300 dark:border-gray-600 px-4 py-3 text-gray-900 dark:text-white bg-white dark:bg-gray-700 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20 transition-all duration-200 shadow-sm"
                      />
                    </div>
                  </div>
                </div>
              </div>

              <div>
                <label
                  htmlFor="notes"
                  className="block text-sm font-medium text-gray-700 dark:text-gray-300"
                >
                  Notitie (optioneel)
                </label>
                <div className="mt-1">
                  <textarea
                    id="notes"
                    rows={2}
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    placeholder="bijv. ziek, vakantie, evenement..."
                    className="block w-full rounded-lg border border-gray-300 dark:border-gray-600 px-4 py-3 text-gray-900 dark:text-white bg-white dark:bg-gray-700 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20 transition-all duration-200 shadow-sm text-sm resize-none"
                  />
                </div>
              </div>

              <div className="mt-6 flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={onClose}
                  className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm font-medium text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 dark:focus:ring-offset-gray-800 transition-all duration-200 shadow-sm"
                >
                  Annuleren
                </button>
                <button
                  type="submit"
                  disabled={submitting}
                  className="px-4 py-2 rounded-lg text-sm font-medium text-white bg-accent hover:bg-accent-hover focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-accent disabled:opacity-50 transition-colors duration-200"
                >
                  {submitting ? (
                    <div className="flex items-center">
                      {entry ? 'Opslaan...' : 'Afronden...'}
                    </div>
                  ) : (
                    entry ? 'Opslaan' : 'Afronden'
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}